<template>
    <main-layout>
        <div class="page-head" slot="header">
            <div>
                <div class="page-kicker">Notices &amp; forms · Rent increase</div>
                <h2>Manufactured home parks (RTB-11a)</h2>
            </div>
            <div class="page-actions">
                <router-link class="el-button el-button--default el-button--small" to="/rent-increase">Monthly run &amp; settings</router-link>
                <router-link class="el-button el-button--default el-button--small" to="/forms?type=rtb11a">View park notices</router-link>
            </div>
        </div>

        <section v-if="!isAdmin" class="ri-card"><b>Only administrators can enter park figures.</b></section>
        <template v-else>
        <section class="ri-card ri-howto" v-loading="loading">
            <div class="ri-howto-head">
                <div>
                    <h3>Figures for notices taking effect in</h3>
                    <el-select v-model="year" size="small" class="w110" @change="load">
                        <el-option v-for="y in years" :key="y" :value="y" :label="String(y)"/>
                    </el-select>
                </div>
                <div class="text-muted ri-due" v-if="info.effective_date">
                    Next notices: effective <b>{{ date(info.effective_date) }}</b>, mail by <b>{{ date(info.mail_deadline) }}</b>
                    · rate {{ info.rate !== null ? info.rate + '%' : 'not confirmed' }}
                </div>
            </div>
            <ol class="ri-howto-steps">
                <li>Each park below is laid out like the RTB-11a. Green notes mark what each notice fills by itself from Buildium; you only enter the white boxes.</li>
                <li>From the owner's tax notice and bills, enter the whole park's amounts in section D (or paste the two columns "last year, this year" from a spreadsheet into the first box).
                    Last year's electricity and gas paid by Sterling are filled from Buildium; other Buildium amounts show under the box with "use".</li>
                <li>Check the worked example for one real site (Step 3, 4 and section E), or <b>Preview the notice (PDF)</b> to see the document a tenant gets.</li>
                <li><b>Save</b> keeps the figures; <b>Confirm figures</b> lets the notices use them (unsent drafts are recalculated). Next year, last year's columns are already filled.</li>
                <li><b>Prepare notices now</b> drafts the park's sites that are due, or wait for the monthly run on the 10th.</li>
            </ol>
        </section>

        <div class="rf-tabs" v-if="parks.length">
            <button v-for="p in parks" :key="p.id" type="button" :class="['rf-tab', String(p.id) === selectedId ? 'on' : '']" @click="selectPark(p.id)">
                <b>{{ p.park_name_default || p.name }}</b>
                <span :class="['ri-pill', p.confirmed && !p.dirty ? 'ok' : (p.data ? 'warn' : 'todo')]">{{ statusText(p) }}</span>
            </button>
        </div>
        <section v-for="p in parks" v-if="String(p.id) === selectedId" :key="p.id" :id="'park-' + p.id" class="ri-card ri-park">
            <div class="ri-park-head">
                <div>
                    <h3>{{ p.name || ('Property ' + p.id) }}</h3>
                    <span class="text-muted">{{ p.site_leases }} site lease(s) on a site account · {{ p.units }} unit(s) in Buildium</span>
                </div>
                <span :class="['ri-pill', p.confirmed && !p.dirty ? 'ok' : (p.data ? 'warn' : 'todo')]">{{ statusText(p) }}</span>
            </div>
            <p v-if="p.prefilled_from" class="ri-note">"Last year" is filled from the {{ p.prefilled_from }} figures. Enter this year's amounts.</p>
            <p v-if="p.unsent_drafts" class="ri-note">
                {{ p.unsent_drafts }} notice(s) for {{ year }} not sent yet<span v-if="p.stale_drafts">; {{ p.stale_drafts }} cannot be sent until the figures are confirmed again</span>.
            </p>

            <!-- A / B: filled per notice -->
            <div class="rf-sec">
                <div class="rf-h">A. TO THE TENANT(s) &nbsp;·&nbsp; B. FROM THE LANDLORD(s)</div>
                <p class="rf-sync">Filled for each site from Buildium: tenant names and phone, site # and address. Landlord: the signer's first and last name
                    as entered in Monthly run &amp; settings › Signatures, and the office address.</p>
                <div class="rf-row">
                    <label>Park Name</label>
                    <template v-if="!p.editName && !p.form.park_name">
                        <span>{{ p.park_name_default }}</span>
                        <span class="rf-hint">printed on each notice ("Park Name", section A) · from the Buildium property name ·
                            <a href="#" @click.prevent="$set(p, 'editName', true)">change</a></span>
                    </template>
                    <template v-else>
                        <el-input size="small" maxlength="60" :value="p.form.park_name" @input="setField(p, 'park_name', $event)" :placeholder="p.park_name_default"/>
                        <span class="rf-hint">printed on each notice instead of "{{ p.park_name_default }}"; leave empty to use the Buildium name</span>
                    </template>
                </div>
            </div>

            <!-- C -->
            <div class="rf-sec">
                <div class="rf-h">C. TENANCY AGREEMENT INFORMATION</div>
                <div class="rf-sub">1) Items included in the rent:</div>
                <el-checkbox-group :value="p.form.included" @input="setField(p, 'included', $event)" class="rf-checks">
                    <el-checkbox v-for="it in info.included" :key="it.key" :label="it.key">{{ it.label }}</el-checkbox>
                </el-checkbox-group>
                <div class="rf-others">
                    <span>Other:</span><el-input size="small" :value="p.form.included_other" @input="setField(p, 'included_other', $event)" maxlength="40"/>
                    <span>Other:</span><el-input size="small" :value="p.form.included_other_2" @input="setField(p, 'included_other_2', $event)" maxlength="40"/>
                </div>
                <div class="rf-sub">2) Date of Last Rent Increase</div>
                <p class="rf-sync">Filled for each site from Buildium (line A, or line B when the site has never had an increase).
                    <span v-if="p.sample">Example, site {{ p.sample.site }}: {{ date(p.sample.last_increase_date) }}.</span></p>
            </div>

            <!-- D -->
            <div class="rf-sec">
                <div class="rf-h">D. DETAILED CALCULATION</div>
                <p class="rf-sync" v-if="p.prefilled_from">Column A and D (last year) are filled from the confirmed {{ p.prefilled_from }} figures. Enter this year's amounts (Column B and E).</p>
                <table v-for="g in groups" :key="g.key" class="rf-table">
                    <thead>
                    <tr>
                        <th>{{ g.num }}) {{ g.title }}</th>
                        <th>Last year:<br><small>Column {{ g.cols[0] }}</small></th>
                        <th>This year:<br><small>Column {{ g.cols[1] }}</small></th>
                        <th>Increase: Column {{ g.cols[2] }}<br><small>= Col. {{ g.cols[1] }} - Col. {{ g.cols[0] }}</small></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(line, i) in info[g.key]" :key="line.key">
                        <td class="rf-label">{{ line.label }}</td>
                        <td v-for="col in ['last', 'this']" :key="col">
                            <input class="ri-num" inputmode="decimal" :aria-label="line.label + ' ' + col + ' year'"
                                   :value="cell(p, g.key, line.key, col)"
                                   @input="setCell(p, g.key, line.key, col, $event.target.value)"
                                   @paste="paste(p, g.key, i, col, $event)">
                            <div class="ri-ref ri-synced" v-if="synced(p, g.key, line.key, col)">from Buildium {{ ref(p, g.key, line.key, 'years')[col] }}</div>
                            <div class="ri-ref" v-else-if="ref(p, g.key, line.key, col) !== null">
                                Buildium {{ ref(p, g.key, line.key, 'years')[col] }}<b v-if="ref(p, g.key, line.key, 'years')[col] >= thisCalendarYear" class="ri-ytd"> to date (year not over)</b>:
                                {{ money(Math.round(ref(p, g.key, line.key, col) * 100)) }}
                                <a href="#" @click.prevent="setCell(p, g.key, line.key, col, ref(p, g.key, line.key, col).toFixed(2))">use</a>
                            </div>
                        </td>
                        <td class="ri-money">{{ halfFilled(p, g.key, line.key) ? '—' : money(diff(p, g.key, line.key)) }}</td>
                    </tr>
                    <tr class="ri-total">
                        <td class="rf-label">{{ g.totalLabel }}</td>
                        <td class="ri-money">{{ money(total(p, g.key, 'last')) }}<br><small>Column {{ g.cols[0] }} total</small></td>
                        <td class="ri-money">{{ money(total(p, g.key, 'this')) }}<br><small>Column {{ g.cols[1] }} total</small></td>
                        <td class="ri-money">{{ incomplete(p).length ? '—' : money(total(p, g.key, 'this') - total(p, g.key, 'last')) }}<br><small>Column {{ g.cols[2] }} total</small></td>
                    </tr>
                    </tbody>
                </table>

                <p class="ri-warn" v-if="incomplete(p).length">Not complete: {{ incomplete(p).join(', ') }} — enter both last year and this year
                    (0 if there was none). The increase and the boxes below are not final until then.</p>
                <div class="rf-sub">3) Calculation of Annual Rent</div>
                <div class="rf-step">Step 1: calculate proportion of local government levies and public utility fees for the park</div>
                <table class="rf-table rf-compact">
                    <tbody>
                    <tr><td class="rf-label">Total Local Government Levies</td><td class="ri-money">{{ money(total(p, 'levies', 'last')) }}<br><small>Column A total</small></td>
                        <td class="ri-money">{{ money(total(p, 'levies', 'this')) }}<br><small>Column B total</small></td>
                        <td class="ri-money">{{ incomplete(p).length ? '—' : money(total(p, 'levies', 'this') - total(p, 'levies', 'last')) }}<br><small>Column C total</small></td></tr>
                    <tr><td class="rf-label">Total Public Utility Fees</td><td class="ri-money">{{ money(total(p, 'utilities', 'last')) }}<br><small>Column D total</small></td>
                        <td class="ri-money">{{ money(total(p, 'utilities', 'this')) }}<br><small>Column E total</small></td>
                        <td class="ri-money">{{ incomplete(p).length ? '—' : money(total(p, 'utilities', 'this') - total(p, 'utilities', 'last')) }}<br><small>Column F total</small></td></tr>
                    <tr class="ri-total"><td class="rf-label">Total Levies &amp; Fees</td>
                        <td class="ri-money">{{ money(total(p, 'levies', 'last') + total(p, 'utilities', 'last')) }}<br><small>Col. A + Col. D</small></td>
                        <td class="ri-money">{{ money(total(p, 'levies', 'this') + total(p, 'utilities', 'this')) }}<br><small>Col. B + Col. E</small></td>
                        <td class="ri-money">{{ incomplete(p).length ? '—' : money(boxA(p)) }}<br><small>Col. C + Col. F</small></td></tr>
                    </tbody>
                </table>
                <div class="rf-step">Step 2: calculate proportion of local government levies and public utility fees for each unit</div>
                <div class="rf-boxes">
                    <div><span>A) Total increase in levies and fees (Column C + Column F)</span><em>Box A</em><b>{{ incomplete(p).length ? '—' : money(boxA(p)) }}</b></div>
                    <div><span>B) Total number of sites in the manufactured home park</span><em>Box B</em>
                        <input class="ri-num w90" inputmode="numeric" aria-label="Box B total sites" :value="p.form.sites_total" @input="setField(p, 'sites_total', $event.target.value)">
                        <a href="#" class="rf-hint" v-if="!p.form.sites_total && p.units" @click.prevent="setField(p, 'sites_total', p.units)">use {{ p.units }} (units in Buildium; {{ p.site_leases }} site leases)</a></div>
                    <div><span>C) Proportion of levies and fees per site (Box A ÷ Box B)</span><em>Box C</em><b>{{ boxReady(p) ? money(boxC(p)) : '—' }}</b></div>
                    <div><span>D) Number of sites assigned to this tenant</span><em>Box D</em><b>1</b></div>
                    <div><span>E) Proportion per site times the number of sites for this tenant (Box C x Box D = Box 3 below)</span><em>Box E</em><b>{{ boxReady(p) ? money(boxC(p)) : '—' }}</b></div>
                </div>
                <template v-if="p.sample">
                    <div class="rf-step">Step 3 and 4 — worked example for site {{ p.sample.site }} ({{ money(Math.round(p.sample.rent_monthly * 100)) }} {{ p.sample.rent_cycle === 'Monthly' ? 'a month' : p.sample.rent_cycle }}, from Buildium); each notice uses its own site's rent</div>
                    <div class="rf-boxes rf-four">
                        <div><em>Box 1</em><b>{{ money(ex(p).b1) }}</b><span>Current annual rent</span></div>
                        <div><em>Box 2</em><b>{{ money(ex(p).b2) }}</b><span>Inflation increase (Allowable {{ info.rate !== null ? info.rate : '?' }}% of Box 1)</span></div>
                        <div><em>Box 3</em><b>{{ money(ex(p).b3) }}</b><span>Proportionate increase in local government levies and public utility fees (Box E above)</span></div>
                        <div><em>Box 4</em><b>{{ money(ex(p).b4) }}</b><span>Maximum total rent for the next 12 months = Box 1 + Box 2 + Box 3</span></div>
                    </div>
                    <div class="rf-boxes">
                        <div><span>Maximum annual rent for the coming 12 months (Box 4 in Step 3)</span><em>Box 5</em><b>{{ money(ex(p).b4) }}</b></div>
                        <div><span>Current annual rent (Box 1 in Step 3)</span><em>Box 6</em><b>{{ money(ex(p).b1) }}</b></div>
                        <div><span>Maximum increase in annual rent (Box 5 - Box 6)</span><em>Box 7</em><b>{{ money(ex(p).b4 - ex(p).b1) }}</b></div>
                    </div>
                </template>
            </div>

            <!-- E -->
            <div class="rf-sec" v-if="p.sample">
                <div class="rf-h">E. AMOUNT OF RENT INCREASE</div>
                <div class="rf-boxes rf-three">
                    <div><span>Your current rent is (Box 6 ÷ 12)</span><b>{{ money(Math.round(ex(p).b1 / 12)) }}</b></div>
                    <div><span>Your new rent will be (Box 5 ÷ 12)</span><b>{{ money(Math.round(ex(p).b4 / 12)) }}</b></div>
                    <div><span>Your rent increase is</span><b>{{ money(Math.round(ex(p).b4 / 12) - Math.round(ex(p).b1 / 12)) }}</b></div>
                </div>
                <p class="rf-sync">Payable starting on the notice's effective date ({{ date(info.effective_date) }} for the next notices).</p>
            </div>

            <!-- F -->
            <div class="rf-sec">
                <div class="rf-h">F. DOCUMENTATION</div>
                <p class="rf-note">The landlord must provide access to a complete set of local government levy invoices, public utility bills and assessment notices. These may be
                    posted in a common area for all tenants, but the landlord must provide a tenant with copies upon request. This documentation is:</p>
                <el-radio-group :value="p.form.documentation.method" @input="setDoc(p, 'method', $event)" class="rf-radios">
                    <el-radio label="attached">Attached to this notice</el-radio>
                    <el-radio label="posted">Posted in common area (please specify)</el-radio>
                    <el-radio label="other">Other</el-radio>
                </el-radio-group>
                <el-input v-if="p.form.documentation.method !== 'attached'" size="small" maxlength="60" class="rf-docdetail" :value="p.form.documentation.detail"
                          @input="setDoc(p, 'detail', $event)" :placeholder="p.form.documentation.method === 'posted' ? 'e.g. Park office bulletin board' : 'Describe'"/>
            </div>
            <p class="rf-sync">G. LANDLORD'S SIGNATURE: the rental manager's name and signature, dated the day the notice is prepared.</p>

            <ol class="rf-flow">
                <li :class="p.confirmed && !p.dirty ? 'done' : 'now'"><b>1. Enter &amp; confirm the figures</b><span>Saves them for {{ year }}. Nothing is sent.</span></li>
                <li :class="p.unsent_drafts ? 'done' : (p.confirmed && !p.dirty ? 'now' : '')"><b>2. Prepare draft notices</b><span>Now, or the monthly run on the 10th. Nothing is sent.</span></li>
                <li><b>3. Review &amp; send</b><span>In <router-link to="/forms?type=rtb11a">Park sites (RTB-11a)</router-link>: open each PDF; a notice goes out only on "Confirm &amp; send".</span></li>
            </ol>
            <div class="ri-actions">
                <el-button size="small" :loading="p.busy === 'save'" @click="save(p, false)">Save</el-button>
                <el-button size="small" :loading="p.busy === 'preview'" :disabled="!p.sample" @click="preview(p)">Preview the notice (PDF)</el-button>
                <el-button size="small" type="primary" :loading="p.busy === 'confirm'" @click="save(p, true)">Confirm figures</el-button>
                <el-button size="small" :disabled="!p.confirmed || p.dirty" :loading="p.busy === 'prepare'" @click="prepare(p)">Prepare draft notices now</el-button>
                <span class="text-muted ri-small" v-if="p.confirmed && !p.dirty && year !== effectiveYear">Prepare works on the {{ effectiveYear }} figures.</span>
            </div>
        </section>
        <section v-if="!loading && !parks.length" class="ri-card">No parks are on the list. Add them under Monthly run &amp; settings › Manufactured home parks.</section>
        </template>
    </main-layout>
</template>

<script>
import ApiService from "../utils/ApiService";
import {formatDate} from "./formHelpers";
import {isAdministrator} from "../layout/navigation";

const cents = v => {
    const n = parseFloat(String(v === null || v === undefined ? '' : v).replace(/[$,\s]/g, ''));
    return isNaN(n) ? 0 : Math.round(n * 100);
};

export default {
    name: "RentIncreaseParks",
    data() {
        return {
            loading: false,
            year: null,
            info: {levies: [], utilities: [], included: [], rate: null, effective_date: '', mail_deadline: ''},
            parks: [],
            groups: [
                {key: 'levies', num: 1, title: 'Local Government Levies', cols: ['A', 'B', 'C'],
                    totalLabel: 'Total Local Government Levies for the entire manufactured home park'},
                {key: 'utilities', num: 2, title: 'Public Utility Fees and Charges', cols: ['D', 'E', 'F'],
                    totalLabel: 'Total public utility fees and charges for the entire manufactured home park'},
            ],
        };
    },
    computed: {
        isAdmin() {
            return isAdministrator(this.$store.getters.userInfo || {});
        },
        // The park on screen: ?park=<id> (from the tags on the run page), else the first one.
        selectedId() {
            const q = this.$route.query.park ? String(this.$route.query.park) : '';
            return this.parks.some(p => String(p.id) === q) ? q : (this.parks[0] ? String(this.parks[0].id) : '');
        },
        thisCalendarYear() {
            return new Date().getFullYear();
        },
        effectiveYear() {
            return this.info.effective_date ? Number(this.info.effective_date.slice(0, 4)) : null;
        },
        years() {
            const base = this.effectiveYear || new Date().getFullYear();
            return [base - 1, base, base + 1];
        },
    },
    methods: {
        date: formatDate,
        money(c) {
            const v = (c || 0) / 100;
            return (v < 0 ? '-$' : '$') + Math.abs(v).toLocaleString('en-CA', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        },
        blankForm(d) {
            d = d || {};
            return {
                levies: JSON.parse(JSON.stringify(d.levies || {})),
                utilities: JSON.parse(JSON.stringify(d.utilities || {})),
                sites_total: d.sites_total || '',
                included: d.included || [],
                included_other: d.included_other || '',
                included_other_2: d.included_other_2 || '',
                documentation: Object.assign({method: 'posted', detail: ''}, d.documentation || {}),
                park_name: d.park_name || '',
            };
        },
        load() {
            this.loading = true;
            ApiService.get('/sterling/rent-increase/parks', {params: this.year ? {year: this.year} : {}}).then(r => {
                this.info = r.data;
                this.year = r.data.year;
                this.parks = r.data.parks.map(p => ({...p, form: this.blankForm(p.data), dirty: false, busy: '', synced: {}}));
                this.parks.forEach(p => this.autoSync(p));

            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                this.loading = false;
            });
        },
        // Buildium reference for a line: amount for 'last' / 'this', or the years.
        ref(p, group, key, col) {
            const r = ((p.buildium || {})[group] || {})[key];
            if (!r) return null;
            if (col === 'years') return r.years || {};
            return r[col] === undefined ? null : r[col];
        },
        // Filled automatically from Buildium on load (shown as "from Buildium <year>"): see autoSync.
        synced(p, group, key, col) {
            return !!p.synced[group + '.' + key + '.' + col];
        },
        /**
         * Sync what is certain: last year's (Column D) electricity and natural gas, when Sterling paid them and that
         * calendar year is over, into empty boxes of a park whose figures are not confirmed. Garbage and water &
         * sewer stay a suggestion ("use"): whether a bill is a local government levy or a private contract is a
         * judgement the office makes.
         */
        autoSync(p) {
            if (p.confirmed) return;
            [['utilities', 'electricity'], ['utilities', 'natural_gas']].forEach(([g, k]) => {
                const years = this.ref(p, g, k, 'years') || {};
                const v = this.ref(p, g, k, 'last');
                if (v === null || !years.last || years.last >= this.thisCalendarYear || this.cell(p, g, k, 'last') !== '') return;
                this.setCell(p, g, k, 'last', v.toFixed(2));
                this.$set(p.synced, g + '.' + k + '.last', true);
            });
            // Filled again on every load, so it is not an unsaved change of the user's.
            p.dirty = false;
        },
        statusText(p) {
            return p.confirmed && !p.dirty ? 'Confirmed' : (p.dirty ? 'Changed, not saved' : (p.data && !p.prefilled_from ? 'Saved, not confirmed' : 'Not entered'));
        },
        selectPark(id) {
            const cur = this.parks.find(x => String(x.id) === this.selectedId);
            const go = () => this.$router.replace({query: {...this.$route.query, park: String(id)}}).catch(() => {});
            if (cur && cur.dirty && String(id) !== this.selectedId) {
                this.$confirm(`${cur.park_name_default || cur.name} has changes that are not saved. Leave them and switch?`, 'Unsaved changes',
                    {confirmButtonText: 'Switch', cancelButtonText: 'Stay'}).then(go).catch(() => {});
                return;
            }
            go();
        },
        // A line with only one of the two years entered: its increase is not known yet.
        halfFilled(p, group, key) {
            const row = p.form[group][key] || {};
            const has = v => v !== '' && v !== null && v !== undefined;
            return has(row.last) !== has(row.this);
        },
        incomplete(p) {
            return this.groups.reduce((all, g) => all.concat(this.info[g.key].filter(l => this.halfFilled(p, g.key, l.key)).map(l => l.label)), []);
        },
        boxReady(p) {
            return parseInt(p.form.sites_total, 10) > 0 && !this.incomplete(p).length;
        },
        // Step 3 and 4 for the sample site, in cents.
        ex(p) {
            const s = p.sample || {};
            const perYear = {Monthly: 12, Weekly: 52, Every2Weeks: 26, Quarterly: 4, Yearly: 1, Annually: 1}[s.rent_cycle] || 12;
            const b1 = Math.round((s.rent_monthly || 0) * 100) * perYear;
            const b2 = Math.round(b1 * (this.info.rate || 0) / 100);
            const b3 = this.boxReady(p) ? this.boxC(p) : 0;
            return {b1, b2, b3, b4: b1 + b2 + b3};
        },
        preview(p) {
            p.busy = 'preview';
            ApiService.post('/sterling/rent-increase/config', {action: 'preview_park_notice', property_id: p.id, year: this.year, data: p.form}).then(r => {
                const bytes = Uint8Array.from(atob(r.data.pdf), c => c.charCodeAt(0));
                const url = URL.createObjectURL(new Blob([bytes], {type: 'application/pdf'}));
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                p.busy = '';
            });
        },
        cell(p, group, key, col) {
            const row = p.form[group][key];
            return row && row[col] !== null && row[col] !== undefined ? row[col] : '';
        },
        setCell(p, group, key, col, value) {
            const row = Object.assign({last: '', this: ''}, p.form[group][key] || {});
            row[col] = value;
            if (p.synced) this.$delete(p.synced, group + '.' + key + '.' + col);
            this.$set(p.form[group], key, row);
            p.dirty = true;
        },
        setField(p, field, value) {
            p.form[field] = value;
            p.dirty = true;
        },
        setDoc(p, field, value) {
            p.form.documentation = Object.assign({}, p.form.documentation, {[field]: value});
            p.dirty = true;
        },
        // A block copied from a spreadsheet: one line per row down the table, tab-separated cells across (last, this).
        paste(p, group, start, col, e) {
            const text = (e.clipboardData || window.clipboardData).getData('text');
            if (!/[\t\n]/.test(text.trim())) return;
            e.preventDefault();
            // Rows as shown: hidden items are skipped, so a pasted column lands on the rows the user sees.
            const lines = this.groups.reduce((all, g) => all.concat(this.info[g.key].map(l => ({group: g.key, key: l.key}))), []);
            const startKey = this.info[group][start].key;
            let at = lines.findIndex(l => l.group === group && l.key === startKey);
            const cols = ['last', 'this'];
            text.replace(/\r/g, '').replace(/\n$/, '').split('\n').forEach(line => {
                const target = lines[at++];
                if (!target) return;
                line.split('\t').slice(0, 2 - cols.indexOf(col)).forEach((v, j) => {
                    const clean = v.replace(/[$,\s]/g, '');
                    this.setCell(p, target.group, target.key, cols[cols.indexOf(col) + j], clean === '-' ? '' : clean);
                });
            });
        },
        diff(p, group, key) {
            const row = p.form[group][key];
            return row ? cents(row.this) - cents(row.last) : 0;
        },
        total(p, group, col) {
            return Object.values(p.form[group]).reduce((n, row) => n + cents(row && row[col]), 0);
        },
        boxA(p) {
            return this.groups.reduce((n, g) => n + this.total(p, g.key, 'this') - this.total(p, g.key, 'last'), 0);
        },
        boxC(p) {
            const sites = parseInt(p.form.sites_total, 10);
            return sites > 0 ? Math.round(this.boxA(p) / sites) : 0;
        },
        save(p, confirm) {
            const go = () => {
                p.busy = confirm ? 'confirm' : 'save';
                return ApiService.post('/sterling/rent-increase/config', {action: 'park_data', property_id: p.id, year: this.year, data: p.form, confirm}).then(r => {
                    const d = r.data;
                    this.$message.success(confirm
                        ? `Confirmed. ${d.drafts_recalculated} unsent notice(s) recalculated` + (d.confirmations_reset ? `; ${d.confirmations_reset} need their manager's confirmation again` : '')
                        : 'Saved. Notices use the figures only after "Confirm figures".');
                    this.load();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    p.busy = '';
                });
            };
            if (!confirm) return go();
            const missing = [];
            if (!(parseInt(p.form.sites_total, 10) > 0)) missing.push('Box B: the total number of sites in the park');
            this.incomplete(p).forEach(l => missing.push(l + ': both last year and this year (0 if there was none)'));
            if (!this.groups.some(g => this.info[g.key].some(l => this.cell(p, g.key, l.key, 'this') !== ''))) missing.push('Section D: the levies and utility fees');
            if (p.form.documentation.method !== 'attached' && !String(p.form.documentation.detail || '').trim()) missing.push('Section F: where tenants can see the documentation');
            if (missing.length) {
                this.$alert('<p>Before the figures can be confirmed, enter:</p><ul><li>' + missing.map(this.esc).join('</li><li>') + '</li></ul>',
                    'Not ready to confirm', {dangerouslyUseHTMLString: true, confirmButtonText: 'OK'});
                return;
            }
            const e = this.ex(p);
            const name = this.esc(p.park_name_default || p.name);
            const html = [
                `<p><b>Nothing is sent to tenants.</b> This saves the figures and lets ${name}'s ${this.year} notices use them.</p>`,
                '<table class="rf-dlg">',
                `<tr><td>Box A · increase, whole park</td><td>${this.money(this.boxA(p))}</td></tr>`,
                `<tr><td>Box B · sites in the park</td><td>${parseInt(p.form.sites_total, 10)}</td></tr>`,
                `<tr><td>Box C · per site per year</td><td>${this.money(this.boxC(p))} (${this.money(Math.round(this.boxC(p) / 12))} a month)</td></tr>`,
                p.sample ? `<tr><td>Example, site ${this.esc(p.sample.site)}</td><td>${this.money(Math.round(e.b1 / 12))} → ${this.money(Math.round(e.b4 / 12))} a month</td></tr>` : '',
                '</table>',
                '<p><b>What happens next</b></p><ol>',
                `<li>"Prepare notices now" (or the monthly run on the 10th) creates draft notices for the sites that are due. Still nothing is sent.</li>`,
                `<li>The drafts appear in Rent increase notices › Park sites (RTB-11a), for the property's manager and the office check. Every notice can be opened as a PDF.</li>`,
                `<li>A tenant gets a notice only when someone presses "Confirm &amp; send" on it.</li></ol>`,
                p.unsent_drafts ? `<p>${p.unsent_drafts} draft(s) already prepared will be recalculated with these figures; a manager who confirmed one confirms it again.</p>` : '',
            ].join('');
            this.$confirm(html, `Confirm the ${this.year} figures`, {confirmButtonText: 'Confirm figures', cancelButtonText: 'Cancel', dangerouslyUseHTMLString: true,
                customClass: 'rf-dlg-box'}).then(go).catch(() => {
            });
        },
        esc(v) {
            return String(v === null || v === undefined ? '' : v).replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
        },
        prepare(p) {
            const name = this.esc(p.park_name_default || p.name);
            this.$confirm(`<p><b>Nothing is sent to tenants.</b> This creates a draft RTB-11a for each site of ${name} whose increase can take effect on
                ${this.date(this.info.effective_date)}.</p><p>Then review them in Rent increase notices › Park sites (RTB-11a): open each as a PDF; the
                property's manager (or the office check) confirms, and a notice goes to a tenant only on "Confirm &amp; send". Last day to mail them:
                ${this.date(this.info.mail_deadline)}.</p>`,
                'Prepare draft notices', {confirmButtonText: 'Prepare drafts', dangerouslyUseHTMLString: true}).then(() => {
                p.busy = 'prepare';
                ApiService.post('/sterling/rent-increase/config', {action: 'prepare_park_notices', property_id: p.id}).then(r => {
                    this.$confirm(this.esc(r.data.result || 'Done') + '<br><br>Drafts appear in the list within a minute.', 'Draft notices',
                        {confirmButtonText: 'Review the drafts', cancelButtonText: 'Close', dangerouslyUseHTMLString: true})
                        .then(() => this.$router.push('/forms?type=rtb11a')).catch(() => {});
                    this.load();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    p.busy = '';
                });
            }).catch(() => {
            });
        },
    },
    // userInfo arrives after the page mounts when it is opened directly (refresh, link): load once it says admin.
    watch: {
        isAdmin: {
            immediate: true,
            handler(admin) {
                if (admin && !this.loadedOnce) {
                    this.loadedOnce = true;
                    this.load();
                }
            },
        },
    },
}
</script>

<style scoped>
.ri-card {
    background: #FFFFFF;
    border: 1px solid #E3E6EE;
    border-radius: 12px;
    padding: 14px 16px;
    margin: 0 6px 12px;
}

.ri-card h3 {
    margin: 0 0 4px;
    font-size: 15px;
    font-weight: 600;
}

.ri-card h4 {
    margin: 14px 0 6px;
    font-size: 14px;
}

.ri-howto-head, .ri-park-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
}

.ri-howto-steps {
    margin: 10px 0 0;
    padding-left: 20px;
    color: #5B6478;
    font-size: 14px;
    line-height: 1.6;
}

.ri-due {
    font-size: 13px;
}

.w110 {
    width: 110px;
}

.w90 {
    width: 90px !important;
}

.ri-pill {
    font-size: 12px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 999px;
    white-space: nowrap;
}

.ri-pill.ok {
    background: #E5F5EA;
    color: #1E7B3A;
}

.ri-pill.warn {
    background: #FFF1D6;
    color: #8A5A00;
}

.ri-pill.todo {
    background: #FDE8E8;
    color: #A12A2A;
}

.ri-note {
    margin: 8px 0 0;
    padding: 8px 10px;
    background: #EEF2FB;
    border-radius: 8px;
    font-size: 13px;
}

.ri-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    font-size: 13px;
}

.ri-table th {
    text-align: left;
    color: #5B6478;
    font-weight: 600;
    padding: 6px 8px;
    border-bottom: 1px solid #E3E6EE;
}

.ri-table td {
    padding: 3px 8px;
    border-bottom: 1px solid #F1F2F6;
}

.ri-table th:nth-child(n+2), .ri-money {
    text-align: right;
    white-space: nowrap;
}

.ri-group td {
    padding-top: 10px;
    font-weight: 600;
    color: #1C2433;
}

.ri-total td {
    font-weight: 600;
    background: #F7F8FB;
}

.ri-num {
    width: 100%;
    max-width: 140px;
    padding: 5px 8px;
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    text-align: right;
    font-size: 13px;
}

.ri-ref {
    margin-top: 2px;
    font-size: 11px;
    color: #8A93A6;
    white-space: nowrap;
}

.ri-ytd {
    color: #B45309;
    font-weight: 600;
}

.ri-warn {
    margin: 10px 0 0;
    padding: 8px 10px;
    border-radius: 8px;
    background: #FFF4E5;
    color: #8A5A00;
    font-size: 13px;
}

.rf-flow {
    list-style: none;
    padding: 0;
    margin: 16px 0 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}

.rf-flow li {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 10px;
    border: 1px solid #E3E6EE;
    border-radius: 8px;
    font-size: 12px;
    color: #5B6478;
}

.rf-flow li b {
    color: #1C2433;
    font-size: 13px;
}

.rf-flow li.done {
    border-color: #A7D8B4;
    background: #F3FBF5;
}

.rf-flow li.now {
    border-color: #F4C27A;
    background: #FFF8EB;
}

.rf-tabs {
    display: flex;
    gap: 8px;
    margin: 0 6px 12px;
    flex-wrap: wrap;
}

.rf-tab {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #FFFFFF;
    border: 1px solid #E3E6EE;
    border-radius: 10px;
    cursor: pointer;
    font: inherit;
    font-size: 14px;
    color: #1C2433;
}

.rf-tab.on {
    border-color: #1F6FEB;
    box-shadow: 0 0 0 2px rgba(31, 111, 235, .15);
}

.rf-sec {
    margin-top: 14px;
    padding-top: 10px;
    border-top: 3px solid #1F4E9C;
}

.rf-h {
    font-weight: 700;
    font-size: 14px;
    letter-spacing: .02em;
    color: #1C2433;
}

.rf-sub {
    margin: 10px 0 6px;
    font-weight: 700;
    font-size: 13px;
}

.rf-step {
    margin: 12px 0 6px;
    font-weight: 600;
    font-size: 13px;
}

.rf-sync {
    margin: 4px 0;
    padding: 6px 10px;
    border-left: 3px solid #A7D8B4;
    background: #F3FBF5;
    font-size: 12px;
    color: #3D5246;
}

.rf-note {
    font-size: 12px;
    color: #5B6478;
    margin: 6px 0;
}

.rf-hint {
    font-size: 12px;
    color: #8A93A6;
}

.rf-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
    flex-wrap: wrap;
}

.rf-row label {
    font-weight: 600;
    font-size: 13px;
}

.rf-row .el-input {
    max-width: 340px;
}

.rf-checks {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 6px 12px;
}

.rf-checks .el-checkbox {
    margin-right: 0;
}

.rf-others {
    display: grid;
    grid-template-columns: auto 1fr auto 1fr;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    font-size: 13px;
}

.rf-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    font-size: 13px;
}

.rf-table th {
    text-align: right;
    font-weight: 600;
    padding: 6px 8px;
    color: #1C2433;
    vertical-align: bottom;
}

.rf-table th:first-child {
    text-align: left;
}

.rf-table th small, .rf-table td small {
    font-weight: 400;
    color: #8A93A6;
    font-size: 11px;
}

.rf-table td {
    padding: 3px 8px;
    border-bottom: 1px solid #F1F2F6;
    text-align: right;
}

.rf-label {
    text-align: right;
    color: #1C2433;
    width: 40%;
}

.rf-table td:nth-child(2), .rf-table td:nth-child(3) {
    width: 18%;
}

.rf-compact td {
    padding: 6px 8px;
}

.rf-boxes {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
}

.rf-boxes > div {
    display: grid;
    grid-template-columns: 1fr 60px 150px;
    align-items: center;
    gap: 10px;
    padding: 4px 0;
    border-bottom: 1px solid #F1F2F6;
}

.rf-boxes em {
    font-style: normal;
    font-weight: 700;
    text-align: right;
}

.rf-boxes b {
    text-align: right;
    font-size: 14px;
}

.rf-four, .rf-three {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
}

.rf-three {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.rf-four > div, .rf-three > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 10px;
    border: 1px solid #E3E6EE;
    border-radius: 8px;
    border-bottom: 1px solid #E3E6EE;
}

.rf-four em {
    text-align: left;
}

.rf-four b, .rf-three b {
    text-align: left;
    font-size: 16px;
}

.rf-four span, .rf-three span {
    font-size: 12px;
    color: #5B6478;
}

.rf-radios {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    margin: 6px 0;
}

.rf-docdetail {
    max-width: 420px;
}

.ri-synced {
    color: #1E7B3A;
    font-weight: 600;
}

.ri-num:focus {
    outline: none;
    border-color: #1F6FEB;
}

.ri-calc {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
}

.ri-calc div {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #F7F8FB;
    font-size: 13px;
    color: #5B6478;
}

.ri-calc b {
    font-size: 16px;
    color: #1C2433;
}

.ri-small {
    font-size: 12px;
}

.ri-two {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
}

.ri-checks {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px 12px;
}

.ri-checks .el-checkbox {
    margin-right: 0;
}

.ri-radios {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 8px;
}

.ri-inline {
    display: flex;
    gap: 8px;
    margin-top: 8px;
}

.ri-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid #EEF0F5;
}

.ri-actions .el-button + .el-button {
    margin-left: 0;
}

@media (max-width: 760px) {
    .rf-checks {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .rf-four, .rf-three {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .rf-boxes > div {
        grid-template-columns: 1fr 50px 110px;
    }

    .ri-calc, .ri-two {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .ri-two {
        grid-template-columns: minmax(0, 1fr);
    }

    .ri-table th:first-child, .ri-table td:first-child {
        min-width: 120px;
    }
}
</style>

<style>
.rf-dlg-box {
    width: 520px;
    max-width: 92vw;
}

.rf-dlg {
    width: 100%;
    border-collapse: collapse;
    margin: 6px 0 10px;
    font-size: 13px;
}

.rf-dlg td {
    padding: 4px 0;
    border-bottom: 1px solid #EEF0F5;
}

.rf-dlg td:last-child {
    text-align: right;
    font-weight: 600;
}
</style>
